"use client";

import { BookADemoModal } from "@/app/(public)/shared/components/BookADemoModal";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { LottiePlayerButton } from "@/app/(public)/shared/ui/LottiePlayerButton";
import { useLottieProgress } from "@/app/(public)/shared/hooks/useLottieProgress";
import Image from "next/image";
import Link from "next/link";
import Lottie from "lottie-react";
import { useInView } from "react-intersection-observer";
import { useRef, useEffect, useState } from "react";
import { LottieRefCurrentProps } from "lottie-react";
import flightAnimation from "@/app/(public)/shared/assets/animations/flight.json";
import venuesAnimation from "@/app/(public)/shared/assets/animations/venues.json";

export const Feature1 = () => {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  // Flight animation refs and observers
  const flightLottieRef = useRef<LottieRefCurrentProps>(null);
  const [isFlightPlaying, setIsFlightPlaying] = useState(true);
  const { ref: flightRef, inView: flightInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // Venues animation refs and observers
  const venuesLottieRef = useRef<LottieRefCurrentProps>(null);
  const [isVenuesPlaying, setIsVenuesPlaying] = useState(true);
  const { ref: venuesRef, inView: venuesInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  // Progress tracking using custom hook
  const flightProgress = useLottieProgress(flightLottieRef, flightInView, isFlightPlaying);
  const venuesProgress = useLottieProgress(venuesLottieRef, venuesInView, isVenuesPlaying);

  useEffect(() => {
    if (flightInView && isFlightPlaying) {
      flightLottieRef.current?.play();
    } else {
      flightLottieRef.current?.pause();
    }
  }, [flightInView, isFlightPlaying]);

  useEffect(() => {
    if (venuesInView && isVenuesPlaying) {
      venuesLottieRef.current?.play();
    } else {
      venuesLottieRef.current?.pause();
    }
  }, [venuesInView, isVenuesPlaying]);

  const toggleFlightPlayback = () => {
    setIsFlightPlaying(!isFlightPlaying);
  };

  const toggleVenuesPlayback = () => {
    setIsVenuesPlaying(!isVenuesPlaying);
  };

  return (
    <div className="w-full flex justify-center pb-12 md:pb-24">
      <div className="w-full md:bg-landing-primary md:rounded-3xl flex flex-col gap-8 md:gap-12 md:shadow-[0_4px_100px_rgba(232,230,241,0.5)] md:p-16">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-8">
          <div className="flex flex-col gap-6 md:gap-8">
            <div className="flex flex-col justify-start items-start gap-4">
              <h2 className="text-landing-primary text-h2 max-w-[530px]">
                Stop wasting weeks
                <br />
                on planning
              </h2>
              <p className="max-w-[530px] text-landing-secondary text-body-lg">
                Corporate events take weeks of emails, research, and spreadsheets. Nowadays makes it effortless—from
                first idea to final invoice.
              </p>
            </div>
            <div className="flex gap-3 mt-2">
              <Link href="/create">
                <Button variant="primary" color="primary" size="md">
                  Try our platform
                </Button>
              </Link>
              <Button variant="ghost" color="neutral" size="md" onClick={() => openModal("demo")}>
                Book a Demo
              </Button>
            </div>
          </div>
          {/* Certifications */}
          <div className="flex flex-row items-center gap-6 lg:flex-1 lg:justify-center lg:gap-16">
            <Link href="https://www.iata.org/" target="_blank" rel="noopener noreferrer">
              <Image
                src="/landing/iata.svg"
                width={160}
                height={53}
                alt="IATA Accredited Agent"
                className="opacity-70 hover:opacity-100 transition-opacity w-[120px] md:w-[140px] lg:w-[160px] h-auto"
              />
            </Link>
            <Link href="https://www.wbenc.org/" target="_blank" rel="noopener noreferrer">
              <Image
                src="/landing/wbenc-certified.webp"
                width={160}
                height={91}
                alt="WBENC Certified Women's Business Enterprise"
                className="opacity-70 hover:opacity-100 transition-opacity w-[120px] md:w-[140px] lg:w-[160px] h-auto"
              />
            </Link>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-6">
          {/* Main Illustration */}
          {/* <div className="h-[499px] bg-landing-secondary rounded-2xl" /> */}

          {/* Bottom Features */}
          <div className="flex flex-col gap-8 md:gap-16">
            <div className="h-px bg-border-landing-primary" />

            {/* Feature 1 - Flight & Budget Estimator */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-16 items-center">
              <div className="flex-1">
                <div
                  ref={flightRef}
                  className="bg-landing-secondary rounded-2xl flex justify-center items-center relative"
                >
                  <Lottie
                    lottieRef={flightLottieRef}
                    animationData={flightAnimation}
                    loop={true}
                    autoplay={false}
                    style={{ width: "100%", pointerEvents: "none" }}
                  />
                  <LottiePlayerButton
                    isPlaying={isFlightPlaying}
                    progress={flightProgress}
                    onClick={toggleFlightPlayback}
                    ariaLabel={isFlightPlaying ? "Pause animation" : "Play animation"}
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-6 max-w-[400px]">
                <div className="flex flex-col gap-2">
                  <h3 className="text-landing-primary text-h3">Before you pick a city, we'll help you choose one</h3>
                  <p className="text-landing-secondary text-body">
                    Flight & Budget Estimator - Instantly compare travel costs across cities using your actual attendee
                    list, travel dates, and preferences. See what your budget gets you—from flights to venues to
                    activities.
                  </p>
                </div>
                <div>
                  <Link href="/flight-estimator" target="_blank">
                    <Button variant="secondary" color="primary" size="sm">
                      Learn more
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-border-landing-primary" />

            {/* Feature 2 - Venue Sourcing */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-16 items-center">
              <div className="flex-1 flex flex-col gap-6 max-w-[400px] order-2 md:order-1">
                <div className="flex flex-col gap-2">
                  <h3 className="text-landing-primary text-h3">Don't spend weeks sourcing venues & vendors</h3>
                  <p className="text-landing-secondary text-body">
                    Search our database of 400K+ vetted venues based on your exact needs. Instantly source venues, A/V,
                    catering, transportation, and more in one place.
                  </p>
                </div>
                <div>
                  <Link href="/venue-sourcing" target="_blank">
                    <Button variant="secondary" color="primary" size="sm">
                      Learn more
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="flex-1 order-1 md:order-2">
                <div
                  ref={venuesRef}
                  className="bg-landing-secondary rounded-2xl flex justify-center items-center relative"
                >
                  <Lottie
                    lottieRef={venuesLottieRef}
                    animationData={venuesAnimation}
                    loop={true}
                    autoplay={false}
                    style={{ width: "100%", pointerEvents: "none" }}
                  />
                  <LottiePlayerButton
                    isPlaying={isVenuesPlaying}
                    progress={venuesProgress}
                    onClick={toggleVenuesPlayback}
                    ariaLabel={isVenuesPlaying ? "Pause animation" : "Play animation"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Modals */}
      <BookADemoModal isOpen={isModalOpen("demo")} onClose={() => closeModal("demo")} />

      <UnifiedModal
        type="content"
        isOpen={isModalOpen("video")}
        onClose={() => closeModal("video")}
        title="Watch Demo Video"
        subtitle="See Nowadays in action"
        size="lg"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-4">
          <iframe
            className="w-full h-[70vh] rounded-xl"
            src="https://www.youtube.com/embed/SS2bcogWkeE"
            title="Nowadays - AI for Event Planning (YC S23)"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      </UnifiedModal>
    </div>
  );
};
